<template>
  <div class="__custom-input" :class="{ horizontal: horizontal }">
    <div class="label" v-if="label">
      <span>{{ label }}</span>
      <!--<HelpPopover placement="top" v-if="helpMsg">{{helpMsg}}</HelpPopover>-->
    </div>

    <div class="input-area" v-if="inputType !== 'textarea'">
      <div
        class="input"
        :class="{ disabled: disabled, search: search, 'type-password': type === 'password' }"
      >
        <input
          :type="inputType"
          v-bind="$attrs"
          :value="currentValue"
          ref="input"
          :placeholder="placeholder"
          :disabled="disabled"
          :class="{ disabled: disabled, 'is-error': errors.has(name) }"
          :name="name"
          v-validate="rules"
          :data-vv-validate-on="action"
          :readonly="readonly"
          @input="handleInput"
          @change="handleChange"
        />

        <div class="search-icon" v-if="search">
          <Icon name="search" />
        </div>

        <div class="eye-btn" @click="handleShowPassword" v-if="type === 'password'">
          <Icon name="eye" type="far" v-if="showPassword" />
          <Icon name="eye-slash" type="far" v-else />
        </div>
      </div>

      <div class="description">
        <span class="description-msg" v-if="showDescription">{{ descriptionMsg }}</span>
        <span class="error-msg" v-else-if="error">{{ errorMsg }}</span>
        <span class="error-msg" v-else-if="errors.has(name)">{{ errorMsg }}</span>
      </div>
    </div>

    <div class="text-area" v-else>
      <textarea
        :type="inputType"
        v-bind="$attrs"
        :value="currentValue"
        ref="input"
        :placeholder="placeholder"
        :disabled="disabled"
        :class="{ disabled: disabled }"
        :name="name"
        @input="handleInput"
        @change="handleChange"
      >
      </textarea>
    </div>
  </div>
</template>

<script>
import Icon from './Icon';
import HelpPopover from './HelpPopover';

export default {
  name: 'Custom-Input',
  components: { Icon, HelpPopover },
  props: {
    //Input
    value: [String, Number],
    type: {
      type: String,
      default: 'text',
    },
    percentage: Boolean,
    placeholder: String,
    disabled: Boolean,
    readonly: Boolean,

    // Validation
    name: {
      type: String,
      default: 'input-box',
    },
    action: {
      type: String,
      default: 'change',
    },

    required: { type: Boolean },
    error: Boolean,

    // Layout
    label: String,
    search: Boolean,
    helpMsg: String,
    descriptionMsg: String,
    errorMsg: String,
    horizontal: Boolean,

    // Validate
    validate: {
      type: Object,
      default: () => {
        return {};
      },
    },

    // Display
    display: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showPassword: false,
    };
  },
  computed: {
    rules() {
      return this.validate;
      // const rule = this.validate;
      // if (this.required) {
      //   if (!rule.required) {
      //     rule["required"] = true;
      //   }
      // }
      // return rule;
    },
    currentValue() {
      if (this.percentage) {
        return Number(this.value) * 100;
      }
      return this.value;
    },
    inputType() {
      if (this.type === 'password') {
        if (this.showPassword) {
          return 'text';
        }
        return 'password';
      }
      if (this.percentage) {
        return 'number';
      }
      return this.type;
    },
    showDescription() {
      return this.descriptionMsg && !this.errorMsg;
    },
  },
  methods: {
    handleInput($event) {
      const value = () => {
        if (this.inputType === 'number') {
          if ($event.target.valueAsNumber) {
            if (this.percentage) {
              return $event.target.valueAsNumber / 100;
            }
            return $event.target.valueAsNumber;
          }
          return 0;
        }
        return $event.target.value;
      };

      this.$emit('input', value());
    },
    handleChange($event) {
      this.$emit('change', $event.target.value);
    },
    handleShowPassword() {
      this.showPassword = !this.showPassword;
    },

    focus() {
      this.$refs['input'].focus();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/mo-custom';

.__custom-input {
  position: relative;
  display: block;
  box-sizing: border-box;
  width: 100%;
  /*padding: 1rem 0;*/
  font-size: 1rem;

  &.horizontal {
    display: flex;

    .label {
      justify-content: flex-end;
      width: 30%;
      padding-right: 2rem;
      margin-bottom: 0;
    }

    .input-area {
      width: 70%;
    }

    .text-area {
      width: 70%;
    }
  }

  .label {
    display: flex;
    align-items: center;
    width: 100%;
    margin-bottom: 0.3em;
  }

  .input-area {
    width: 100%;

    .description {
      position: absolute;
      bottom: -1.1rem;
      font-size: 0.8em;

      .error-msg {
        color: $danger-color;
      }
    }

    .input {
      position: relative;

      input {
        box-sizing: border-box;
        width: 100%;
        height: 32px;
        padding-left: 1em;
        font-size: 0.85em;
        line-height: 2.4em;
        background-color: $foundation-color;
        border: 1px solid $border-color;
        border-radius: 4px;
        /*box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);*/
        transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
      }

      input::placeholder {
        opacity: 0.5;
      }

      input.disabled {
        background-color: darken($muted-color, 10%);
        cursor: not-allowed;

        &::placeholder {
          color: rgba($neutral-color, 0.7);
        }
      }

      input.is-error {
        border: 2px solid $danger-color;
      }

      &.disabled {
        color: rgba($neutral-color, 0.2);
      }

      &.search {
        input {
          padding-left: 2.5rem;
        }
      }

      .search-icon {
        position: absolute;
        top: 0.3rem;
        left: 0.7rem;
        color: rgba($neutral-secondary-color, 0.4);
      }

      &.type-password {
        input {
          padding-right: 3rem;
        }
      }

      .eye-btn {
        position: absolute;
        top: 0.5rem;
        right: 1rem;
        font-size: 1.3em;
        color: rgba($neutral-secondary-color, 0.6);
        cursor: pointer;
      }
    }
  }

  .text-area {
    position: relative;

    textarea {
      width: 100%;
      box-sizing: border-box;
      resize: vertical;
      padding-left: 1em;
      font-size: 1rem;
      line-height: 2.4em;
      background-color: $foundation-color;
      border: 1px solid $secondary-color;
      border-radius: 4px;
      box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
      transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
    }
  }
}
</style>
